import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * "Open the other record" — the one affordance that links a job to its invoice and an
 * invoice back to its job.
 *
 * <p>Both directions render the same thing for the same reason: the number the person
 * already reads on the card becomes the way to get to the other side of it. Written
 * once, because two copies of a link drift into two behaviours — one clickable, one
 * not, on the same pair of records.
 *
 * <h2>🔑 No uuid, no control</h2>
 *
 * A job that has never been invoiced, or an invoice raised with no job behind it (every
 * migrated one is), has nothing to open. Then this renders the number as plain text, or
 * nothing at all when there is no number either — never a dead button. Whether the other
 * record exists is the SERVER's answer, carried as a uuid; it is never inferred from a
 * status.
 *
 * @param {string|null} uuid the other record's uuid, or null when there is none
 * @param {string|null} label what to show — "INV-42", "JOB-20260908-001"
 * @param {string|null} [detail] a word after the label, e.g. "Paid"
 * @param {React.ComponentType} [icon] a lucide icon, drawn before the label
 * @param {(uuid: string) => void} onOpen given the uuid when the control is used
 * @param {string} title the control's tooltip and accessible name
 * @param {boolean} [loading=false] the other record is being fetched: a spinner takes
 *        the icon's place and the control stops accepting presses, so an impatient
 *        second click cannot fire a second request
 * @param {string} [className] wrapper classes, so each screen keeps its own spacing
 * @param {'inline'|'chip'} [variant='chip'] inline reads as part of a sentence; a chip
 *        stands on its own next to the summary tiles
 */
export default function RecordCrossLink({
    uuid, label, detail = null, icon: Icon = null, onOpen, title,
    loading = false, className = '', variant = 'chip',
}) {
    if (!label) {
        return null;
    }

    const iconSize = variant === 'chip' ? 13 : 12;
    const body = (
        <>
            {loading
                ? <Loader2 size={iconSize} className="shrink-0 animate-spin" data-testid="record-cross-link-spinner" />
                : Icon && <Icon size={iconSize} className="shrink-0" />}
            <span className="font-mono">{label}</span>
            {detail && <span className="opacity-75">· {detail}</span>}
        </>
    );

    // Nothing to open: the number is still worth reading, so it stays as text.
    if (!uuid) {
        return (
            <span
                data-testid="record-cross-link-text"
                className={`inline-flex items-center gap-1.5 ${
                    variant === 'chip'
                        ? 'px-2 py-1 rounded-lg bg-gray-50 text-gray-500 text-xs'
                        : 'text-xs text-gray-500'
                } ${className}`}
            >
                {body}
            </span>
        );
    }

    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (!loading) onOpen(uuid); }}
            disabled={loading}
            title={title}
            aria-label={title}
            className={`inline-flex items-center gap-1.5 transition-colors disabled:opacity-70 disabled:cursor-wait ${
                variant === 'chip'
                    ? 'px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold'
                    : 'text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline'
            } ${className}`}
        >
            {body}
        </button>
    );
}
